import { AxiosResponse } from 'axios';
import { LogArgument } from 'rollbar';
import { DEFAULT_MAX_ITEMS, DEVICE_TYPE } from './constants';
import getSessionId from './getSessionId';
import { SETTINGS } from './init';
import {
  apiPostAds,
  generateAdsRequestBody,
  IAdItem,
  ISponsoredAdsResponse,
} from './utils/apiPostAds';
import Rollbar from './utils/rollbar';
import wrapCallbackWithErrorHandling from './utils/wrapCallbackWithErrorHandling';
import { isInWebView } from './webViewBridge/utils';

export interface IRecommendBanner {
  placementId: number;
  maxItems?: number;
}

interface IRecommendBannerResultAd {
  adId: string;
  advertiserId: string;
  productId: string;
  imgUrl: string;
  position: number;
  redirect: { type: string; url: string };
  misc: string;
}

interface IRecommendBannerResult {
  ads: IRecommendBannerResultAd[];
  requestId: string;
}

const recommendBanners = async (
  { placementId, maxItems = DEFAULT_MAX_ITEMS }: IRecommendBanner,
  callback: (err: unknown, data: IRecommendBannerResult | null) => void,
): Promise<void> => {
  try {
    const safeCallback = wrapCallbackWithErrorHandling(callback);

    if (isInWebView() && window.rmnMobileSdk) {
      window.rmnMobileSdk
        .callMobileSdkGetBanners({ placementId, maxItems })
        .then(({ placements, request_id }) => {
          const ads = placements[0].ads;
          const processedAds: IRecommendBannerResultAd[] = ads.map(processAds);
          safeCallback(null, {
            ads: processedAds,
            requestId: request_id,
          });
        })
        .catch(({ errorCode, errorMessage }) => {
          safeCallback(errorMessage, null);
          // Mobile behavior is different from web, mobile will reject the promise if 204 no content.
          if (errorCode !== 204) {
            Rollbar.error(
              `[rmnMobileSdk] recommendBanners: ${errorMessage}(${errorCode})`,
            );
          }
        });
      return;
    }

    const sessionId = getSessionId();

    if (!sessionId) {
      safeCallback('Missing sessionId', null);
      return;
    }

    const requestTimestamp = Date.now();
    const requestBody = generateAdsRequestBody({
      placements: [
        {
          placement_id: placementId,
        },
      ],
      page_number: 0,
      session_id: sessionId,
      max_ads: maxItems,
      customer: { customer_id: sessionId, device_type: DEVICE_TYPE },
    });
    let response: AxiosResponse<ISponsoredAdsResponse>;
    try {
      response = await apiPostAds(requestBody);
    } catch (err) {
      if (err) safeCallback(err, null);
      return;
    }

    if (!response.data) {
      safeCallback(null, null);
      return;
    }

    const ads = response.data.placements[0].ads;
    const requestId = response.headers['x-request-id'];
    const url = document.location.href;
    const referrerUrl = document.referrer;

    // Event schema
    // https://docs.google.com/spreadsheets/d/1MKKxLRgsI9t0W81z11y8nByY0fXBDPL54EUqwk3d_8g/edit#gid=896115465
    const eventParam = {
      product_ids: ads.map(({ creative_id }) => `${creative_id}`),
      advertiser_ids: ads.map(({ advertiser_id }) => advertiser_id),
      ad_ids: ads.map(({ ad_id }) => ad_id),
      positions: ads.map(({ position }) => position),
      campaign_ids: ads.map(({ campaign_id }) => campaign_id),
      miscs: ads.map(({ misc }) => misc),
      request_id: requestId,
      req_ts: requestTimestamp,
      marketplace_id: SETTINGS.MARKET_PLACE_ID,
      placement_id: placementId,

      ...(url ? { url } : {}),
      ...(referrerUrl ? { referrer_url: referrerUrl } : {}),
      user_agent: navigator.userAgent,
    };
    window.appier('event', 'sponsored_ads_showed', eventParam);

    const processedAds: IRecommendBannerResultAd[] = ads.map(processAds);

    safeCallback(null, {
      ads: processedAds,
      requestId,
    });
  } catch (error) {
    Rollbar.error(error as LogArgument);
  }
};

function processAds({
  ad_id,
  advertiser_id,
  creative_id,
  img_url,
  position,
  redirect,
  misc,
}: IAdItem): IRecommendBannerResultAd {
  return {
    adId: ad_id,
    advertiserId: advertiser_id,
    productId: `${creative_id}`,
    imgUrl: img_url,
    position,
    redirect,
    misc,
  };
}

export default recommendBanners;
